import { draftKey, inherited } from "./store";

const keyOf = ({ accessor, Id, id }) => draftKey(accessor, Id ?? id);

// What a form saves (its item and the extra of its page) together with the
// draft of its scope: one request, one commit. A row the form saves itself
// (its item, an item of the extra) wins over its draft entry, as the form
// was loaded from the draft row; it takes the entry's baseVersion when it
// has none. The deletes are deduplicated; the modifiche are the page's.
//
// Order of the items: the rows the draft created first (in the order they
// were created, as collect gives them), then the extra of the page, then
// the other rows of the draft. A created row comes before the rows that
// require it (a new variant N before the endpoint E' that lists it, even
// when E' is the page's extra): the current backend prepares the items in
// order. The item of the form goes before all of them (useItem): when it is
// a row the draft created (a new variant open in its page) it is sent as
// new (isNew, baseVersion 0, see Item); an existing row does not require a
// row created in the same draft.
// A created row that inherits fields from another row (inherit, see collect:
// a new endpoint takes the params of its property at the Save) keeps doing
// so when the page's extra saves it (e.g. the endpoint of a variant page
// with pending conditions): the extra's row is its base. The form's own
// row (e.g. the property, when its Item saves) is not here: inheritFromBatch
// gives them the batch's row once the form's item is in it (useItem).
export function mergeDraft(extra, draftBatch, { accessor, Id } = {}) {
  const extraItems = extra?.items ?? [];
  const draftItems = draftBatch?.items ?? [];
  const draftByKey = new Map(draftItems.map((item) => [keyOf(item), item]));
  const formKey =
    accessor !== undefined && Id !== undefined ? draftKey(accessor, Id) : null;
  const extraKeys = new Set(extraItems.map(keyOf));
  const taken = new Set(formKey ? [formKey] : []);
  const items = [];

  const takeDraft = (item) => {
    const key = keyOf(item);
    if (taken.has(key)) return;
    taken.add(key);
    items.push(item);
  };

  // Created by the draft, unless the form or the page saves it
  for (const item of draftItems)
    if (item.isNew && !extraKeys.has(keyOf(item))) takeDraft(item);

  for (const item of extraItems) {
    const key = keyOf(item);
    const entry = draftByKey.get(key);
    taken.add(key);

    let merged =
      entry && item.baseVersion === undefined && entry.baseVersion !== undefined
        ? { ...item, baseVersion: entry.baseVersion }
        : item;
    if (entry?.inherit && !item.inherit) {
      const inherit = { ...entry.inherit, base: item.data };
      merged = {
        ...merged,
        inherit,
        data: inherited(inherit, inherit.source)
      };
    }
    items.push(merged);
  }

  for (const item of draftItems) takeDraft(item);

  const deletes = [];
  const deleted = new Set();
  for (const del of [
    ...(extra?.deletes ?? []),
    ...(draftBatch?.deletes ?? [])
  ]) {
    const key = keyOf(del);
    if (deleted.has(key) || taken.has(key)) continue;
    deleted.add(key);
    deletes.push(del);
  }

  return {
    items,
    deletes,
    ...(extra?.modifiche !== undefined && { modifiche: extra.modifiche })
  };
}

// The items of a batch, the created rows that inherit fields from another
// row of the batch (inherit, see collect) given that row's fields: e.g. a new
// endpoint gets the params of the property the batch saves, the form's
// values when the property's Item saves. Called on the whole batch (the
// form's item included, useItem); a row that is not in the batch keeps the
// fields collect gave (the draft or stored row). Idempotent.
export function inheritFromBatch(items = []) {
  const rows = new Map(items.map((item) => [keyOf(item), item.data]));

  return items.map((item) => {
    const { inherit } = item;
    if (!inherit) return item;
    const source = rows.get(draftKey(inherit.accessor, inherit.id));
    return source ? { ...item, data: inherited(inherit, source) } : item;
  });
}
